import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { isEqual } from 'es-toolkit';
import type { DefaultComponentProps } from '../types';
import { useSocialFeedSettings } from '../utils';
import type {
  LinkedinPreparedSource,
  SourceValue,
  TiktokPreparedSource,
  TwitterPreparedSourceValue,
  TwitterSourceValue,
  UserValue,
  YoutubePreparedSourceValue,
  YoutubeSourceValue
} from './sources';
import { prepareTwitterProfileSource, prepareYoutubeSource } from './sources';

type SourcesNeedPreparing = YoutubeSourceValue | TwitterSourceValue;

type ExcludedSourceValue = Exclude<SourceValue, SourcesNeedPreparing>;

export type PreparedSourceType =
  | ExcludedSourceValue
  | YoutubePreparedSourceValue
  | TwitterPreparedSourceValue
  | TiktokPreparedSource
  | LinkedinPreparedSource;

type PreparedSourceProviderType = {
  preparedSources: PreparedSourceType[];
  sourcesReady: boolean;
  updateSourceAuthor: (sourceId: string, author: UserValue) => void;
};

const PreparedSourceContext = createContext<PreparedSourceProviderType>({
  preparedSources: [],
  sourcesReady: false,
  updateSourceAuthor: () => {}
});

const prepareMapping = {
  legacyTwitterProfile: prepareTwitterProfileSource,
  youtubeChannel: prepareYoutubeSource,
  youtubePlaylist: prepareYoutubeSource
};

export function PreparedSourceProvider({ children }: DefaultComponentProps) {
  const { sources, widgetToken } = useSocialFeedSettings();
  const [preparedSources, setPreparedSources] = useState<PreparedSourceType[]>(
    []
  );
  const [sourcesReady, setSourcesReady] = useState(false);

  const authorsRef = useRef<Record<string, UserValue>>({});
  const prevSourcesRef = useRef<SourceValue[]>([]);

  useEffect(() => {
    const hasActuallyChanged = !isEqual(prevSourcesRef.current, sources);

    if (hasActuallyChanged) {
      setSourcesReady(false);
      prevSourcesRef.current = sources;
    }
  }, [sources]);

  useEffect(() => {
    let canceled = false;

    (async () => {
      const willPrepared = sources.map((source) => {
        const connectionType = source?.connectionType;

        if (!(connectionType in prepareMapping)) {
          return source;
        }

        // @ts-expect-error Есть трудности в правильной типизации
        const prepareSource = prepareMapping[connectionType];

        return prepareSource(source, widgetToken!);
      });

      const prepared = (await Promise.all(willPrepared)).filter(
        Boolean
      ) as PreparedSourceType[];

      if (!canceled) {
        const merged = prepared.map((source) =>
          authorsRef.current[source.id]
            ? { ...source, author: authorsRef.current[source.id] }
            : source
        );
        setPreparedSources(merged);
        setSourcesReady(true);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [sources, widgetToken]);

  const updateSourceAuthor = useCallback(
    (sourceId: string, author: UserValue) => {
      authorsRef.current[sourceId] = author;
      setPreparedSources((prevSources) =>
        prevSources.map((source) =>
          source.id === sourceId ? { ...source, author } : source
        )
      );
    },
    []
  );

  const value: PreparedSourceProviderType = useMemo(
    () => ({ preparedSources, sourcesReady, updateSourceAuthor }),
    [preparedSources, sourcesReady, updateSourceAuthor]
  );

  return (
    <PreparedSourceContext.Provider value={value}>
      {children}
    </PreparedSourceContext.Provider>
  );
}

export function usePreparedSource() {
  return useContext(PreparedSourceContext);
}
